/**
 * Where a create's last answer is held, so the lock it puts on Create outlives the
 * component that showed it — and nothing more.
 *
 * 🔴 The defect this closes (an open defect of production since twads#234, measured on
 * 67506f4 and recorded in audits/2026-10-29/gui/plan.md, «Відкрита вада проду»): the
 * line item's lock was `useState` inside `LineItems`, so every path that unmounts the
 * section cleared it — the New campaign toggle, Hide line items, another account, another
 * campaign — and Create was offered again after `exists` or `unknown` for the same
 * campaign. X publishes no idempotency key for the create (docs/API-CONTRACT.md, «this field
 * is the whole of the protection»), so the second press is a second line item in X. The
 * campaign's lock had the same shape one level up: `useState` in the Campaigns page, gone
 * when the route unmounts it.
 *
 * Held in this module's memory, keyed by what the lock is about, so a section mounted
 * again for the same campaign reads the same answer. A browser reload starts the module
 * afresh and clears it, which is what `CREATE_LOCK_IS_LOCAL` says and still says truly:
 * «Reloading this page clears the warning — it is kept in the screen, not in the account.»
 * Not `sessionStorage`: that would survive the reload the sentence names.
 *
 * What lifts a lock is unchanged — `outcomeAfterReload`, a new create, or the deliberate
 * «create anyway» — and goes through the same setter, so it is held here too.
 *
 * 🔴 A create that is still out is held as well (`holdInFlight`): set before the request's
 * `await`, lifted in its `finally`. Without it a form unmounted while its request was out
 * came back with no answer and no `busy`, and offered Create for a second send before the
 * first had answered — the gap twads#240 named in its body. Each form watches its key, so
 * the answer that lands later, from the gone form's request, is shown by the one mounted now.
 *
 * A run of Add ad (and of the wizard) is held whole, under its parent. One that finished —
 * its last step attached, so every step before it answered created — is let go the next
 * time its form opens (`finishedRun`): nothing it made is unknown, so there is no duplicate
 * to guard against, and the next ad is a new draft. Stopped or still out, it is kept.
 *
 * 🔴 The wizard makes what the page forms make, so its answers land under their keys too
 * (decided by -3d on 2026-10-30). Its own run is held under the ad account
 * (`createAdLockKey`). A campaign or line item it sends is marked in flight under the key
 * that parent's form reads, and an answer that locks — `exists`, `unknown` — is held
 * there (`holdIfLocking`); one that does not leaves that form as it was. Its ad's part —
 * uploads, card, tweet, promote — is held under the line item as Add ad's own run is
 * (`adPart`), so Add ad on that row reads it.
 */
import { useEffect, useReducer } from "react";
import { type Locking, mayCreateAgain } from "./campaigns.ts";
import type { Run } from "./create-ad.ts";

const held = new Map<string, unknown>();
/** The keys whose create has been sent and not yet answered. */
const flying = new Set<string>();
/** Who shows each key, so an answer that lands after its form went is seen by the next one. */
const watching = new Map<string, Set<() => void>>();

function changed(key: string): void {
	for (const show of watching.get(key) ?? []) show();
}

/** Be told whenever the answer or the mark under `key` changes; returns the way to stop. */
export function watchKey(key: string, show: () => void): () => void {
	const shows = watching.get(key) ?? new Set<() => void>();
	shows.add(show);
	watching.set(key, shows);
	return () => {
		shows.delete(show);
		if (shows.size === 0) watching.delete(key);
	};
}

/** The key of a campaign create's lock: one per ad account. */
export function campaignLockKey(xAccountId: string): string {
	return `campaign:${xAccountId}`;
}

/** The key of a line item create's lock: one per campaign under one ad account. */
export function lineItemLockKey(xAccountId: string, campaignId: string): string {
	return `line-item:${xAccountId}:${campaignId}`;
}

/** The key of Add ad's run on the campaign card: one per line item under one ad account. */
export function addAdLockKey(xAccountId: string, lineItemId: string): string {
	return `add-ad:${xAccountId}:${lineItemId}`;
}

/** The key of the Create ad wizard's run: one per ad account, the account it creates under. */
export function createAdLockKey(xAccountId: string): string {
	return `create-ad:${xAccountId}`;
}

/**
 * Whether a held run is finished, and so let go when its form opens next: the ad attached,
 * and no criterion made without its read-back. The run stops at the first step that is not
 * done, so an attached ad means every step before it answered created — through its
 * read-back for every step but a criterion (docs/API-CONTRACT.md: a failed read-back
 * answers `created: true`, a stop). A criterion that answered `created: true` on another
 * status goes on as done with `readBack: false` (`criterionStep`), so it is named here.
 */
export function finishedRun(run: Run): boolean {
	return (
		run.ad.kind === "done" &&
		!run.targeting.some((t) => t.state.kind === "done" && t.state.readBack === false)
	);
}

/** The ad's part of a run — the uploads, the card, the tweet, the promote: what Add ad holds. */
export function adPart(run: Run): Run {
	return {
		campaign: { kind: "pending" },
		lineItem: { kind: "pending" },
		targeting: [],
		media: run.media,
		card: run.card,
		tweet: run.tweet,
		ad: run.ad,
	};
}

/** The answer held for a key, `null` when none is. */
export function heldOutcome<T>(key: string): T | null {
	return (held.get(key) as T | undefined) ?? null;
}

/** Hold an answer for a key; `null` forgets it. */
export function holdOutcome<T>(key: string, value: T | null): void {
	if (value === null) held.delete(key);
	else held.set(key, value);
	changed(key);
}

/**
 * Hold another form's answer under its key, only when it locks: a created or refused one
 * leaves that form as it was. A locking answer already held there is kept — it is about
 * the draft that form still has filled in, which this answer is not.
 */
export function holdIfLocking<T extends Locking>(key: string, outcome: T): void {
	const now = heldOutcome<Locking>(key);
	if (mayCreateAgain(outcome) || (now !== null && !mayCreateAgain(now))) return;
	holdOutcome(key, outcome);
}

/** Whether a create under this key has been sent and not yet answered. */
export function inFlight(key: string): boolean {
	return flying.has(key);
}

/**
 * Mark a create under this key as sent (before its `await`) or answered (in its
 * `finally`). 🔴 A form unmounted while the request is out leaves the mark standing, so
 * the form mounted next reads it and refuses a second send; the answer lifts it from the
 * old closure, under the same key.
 */
export function holdInFlight(key: string, sent: boolean): void {
	if (sent) flying.add(key);
	else flying.delete(key);
	changed(key);
}

/**
 * Send under a key's in-flight mark: set before, lifted after whatever `send` did — and
 * after `send`'s own handling, so the answer is held before the mark goes.
 */
export async function whileSent<T>(key: string, send: () => Promise<T>): Promise<T> {
	holdInFlight(key, true);
	try {
		return await send();
	} finally {
		holdInFlight(key, false);
	}
}

/** Forget every held answer and every mark — the reload's effect, for a test. */
export function forgetAllOutcomes(): void {
	held.clear();
	flying.clear();
}

/**
 * `useState` for a create's last answer, held in this module under `key`. The value is
 * read from the module on every render, so a new key shows that key's answer at once —
 * never the previous key's for a render. The setter takes a value or a function of the
 * value held now, as `useState`'s does. The form watches its key, so an answer written
 * under it after an earlier form went — by that form's request, still out — is shown.
 */
export function useHeldOutcome<T>(
	key: string,
): [T | null, (next: T | null | ((now: T | null) => T | null)) => void] {
	const [, rendered] = useReducer((n: number) => n + 1, 0);
	useEffect(() => watchKey(key, rendered), [key]);
	const set = (next: T | null | ((now: T | null) => T | null)) => {
		const value =
			typeof next === "function"
				? (next as (now: T | null) => T | null)(heldOutcome<T>(key))
				: next;
		holdOutcome(key, value);
		rendered();
	};
	return [heldOutcome<T>(key), set];
}
